import { VideoRenderDto } from "@repo/api/video-render";
import { useEffect, useRef, useState } from "react";
import {
  ExportVideoDialogState,
  ShareLinkViewState,
} from "./ExportVideoDialog";
import { ShareLinkCard } from "./ShareLinkCard";
import { getStatus, getTitle, getDescription } from "./ShareLinkDetails";
import { DialogHeaderCenter } from "./DialogHeaderCenter";
import AdvertiseVideoFooter from "./AdvertiseVideoFooter";
import { downloadFile } from "@/lib/utils";
import Toast from "@/lib/components/Toast";
import {
  useDeleteVideoRenderShareMutation,
  useShareVideoRenderMutation,
} from "@/lib/queries/video-render-queries";

type Props = {
  dialogState: ShareLinkViewState;
  setDialogState: (state: ExportVideoDialogState) => void;
  videoRenders: VideoRenderDto[] | undefined;
  teamExternalId: string;
  videoExternalId: string;
};

export default function ShareLinkView({
  dialogState,
  setDialogState,
  videoRenders,
  teamExternalId,
  videoExternalId,
}: Props) {
  const videoRender = dialogState.videoRenderExternalId
    ? (videoRenders?.find(
        (render) => render.externalId === dialogState.videoRenderExternalId,
      ) ?? null)
    : null;

  const [hasShareRequestFailed, setHasShareRequestFailed] = useState(false);
  const status = hasShareRequestFailed ? "failed" : getStatus(videoRender);
  const link = videoRender?.shareUrl ?? null;

  const { mutate: shareVideoRender, isPending: isSharing } =
    useShareVideoRenderMutation({
      teamExternalId,
      videoExternalId,
      onMutate: () => setHasShareRequestFailed(false),
      onError: (error) => {
        console.error(error);
        setHasShareRequestFailed(true);
        Toast.error("Failed to create share link");
      },
    });

  // Finished renders without an active share (never shared or previously
  // removed) get a share created automatically when entering this view.
  const needsShare =
    videoRender?.status === "done" &&
    (videoRender.shareInfo === null ||
      videoRender.shareInfo.status === "deleted");
  const hasAutoShared = useRef(false);

  useEffect(() => {
    if (needsShare && videoRender && !hasAutoShared.current) {
      hasAutoShared.current = true;
      shareVideoRender(videoRender.externalId);
    }
  }, [needsShare, videoRender, shareVideoRender]);

  const { mutate: deleteVideoRenderShare, isPending: isRemoving } =
    useDeleteVideoRenderShareMutation({
      teamExternalId,
      videoExternalId,
      onSuccess: () => {
        Toast.info("Public link removed");
        setDialogState({ view: "EXPORT" });
      },
      onError: (error) => {
        console.error(error);
        Toast.error("Failed to remove public link");
      },
    });

  const handleBack = () => {
    if (dialogState.fromView === "EXPORT_QUALITY") {
      setDialogState({ view: "EXPORT_QUALITY", exportType: "LINK" });
      return;
    }

    if (dialogState.fromView === "EXPORT_DETAILS") {
      setDialogState({
        view: "EXPORT_DETAILS",
        videoRenderExternalId: dialogState.videoRenderExternalId!,
        exportType: dialogState.exportType ?? "DOWNLOAD",
        fromView: dialogState.detailsFromView ?? "EXPORT",
      });
      return;
    }

    setDialogState({ view: dialogState.fromView });
  };

  const handleCopy = async () => {
    if (!link) {
      return;
    }

    try {
      await navigator.clipboard.writeText(link);
      Toast.info("Link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      Toast.error("Failed to copy link");
    }
  };

  const handleDownload = () => {
    if (videoRender?.downloadUrl) {
      downloadFile(videoRender.downloadUrl);
    }
  };

  const handleRemove = () => {
    if (videoRender) {
      deleteVideoRenderShare(videoRender.externalId);
    }
  };

  const handleRetry = () => {
    if (videoRender) {
      shareVideoRender(videoRender.externalId);
    }
  };

  return (
    <div className="flex h-full flex-1 flex-col items-center">
      <DialogHeaderCenter
        title={getTitle(status)}
        description={getDescription(status, videoRender)}
        onBack={handleBack}
      />

      <ShareLinkCard
        status={status}
        link={link}
        isRendering={videoRender?.status !== "done"}
        onCopy={handleCopy}
        onDownload={handleDownload}
        onRemove={handleRemove}
        onRetry={videoRender?.status === "done" ? handleRetry : undefined}
        isRemoving={isRemoving}
        isRetrying={isSharing}
      />
      <AdvertiseVideoFooter />
    </div>
  );
}
